/**
 * Fill in the Blanks Content Type — Editor
 * Tabs: Content (title, description, Page) · Questions (sidebar + FITB) · Configuration
 */

import * as React from 'react';
import { Input, Label, Textarea, cn, createEmptyDocument } from '@ai-content-hub/content-kit';
import {
    type EditTabDescriptor,
    type CompositeQuestionsSidebarRow,
    generateId,
    CompositeEditShell,
    CompositeEditContentTab,
    CompositeEditSortableQuestionsLayout,
    orderItemsByQuestionsSidebarRows,
    persistQuestionsSidebarRows,
    reconcileQuestionsSidebarRows,
    resolvedQuestionsSidebarRows,
    pickErrorsUnderPrefix,
    exerciseDataErrorsPrefix,
    hasErrorsForExerciseIndex,
    useLocale,
} from '@ai-content-hub/content-core';
import {
    FITBEdit as FITBElementEdit,
    PageEdit,
    defaultFITBElementData,
    type PageElementData,
    countBlanksInTiptap,
} from '@ai-content-hub/content-elements';
import type { BlockContentType } from '@ai-content-hub/content-elements';
import type { FITBContentData, FITBContentConfig, FITBContentExercise } from './schema';
import {
    defaultFITBContentData,
    normalizeFITBContentData,
    serializableFITBContentData,
} from './schema';
import { FileText, Settings, TextCursorInput } from 'lucide-react';
import map from 'lodash/map';

const PAGE_BLOCK_TYPES: BlockContentType[] = [
    'rich-text',
    'image',
    'audio',
    'office-document',
    'text',
];

const FITB_EXERCISE_TYPE_ID = 'fitb-exercise';

/** Editor display options — matches EditorDisplayOptions from content-core */
interface EditorDisplayOptions {
    showTitle?: boolean;
    showDescription?: boolean;
}

type TabValue = 'content' | 'questions' | 'config';

function exercisePreview(
    ex: FITBContentExercise,
    index: number,
    t: (key: string, defaultMessage?: string) => string
): string {
    const n = countBlanksInTiptap(ex.data?.passage);
    return t('exercisePreview', 'Question {{index}} — {{n}} blanks')
        .replace('{{index}}', String(index + 1))
        .replace('{{n}}', String(n));
}

export interface FITBContentEditProps {
    value: FITBContentData;
    onChange: (value: FITBContentData) => void;
    config?: FITBContentConfig;
    onConfigChange?: (config: FITBContentConfig) => void;
    disabled?: boolean;
    editorOptions?: EditorDisplayOptions;
    configSlot?: React.ReactNode;
    className?: string;
    errors?: Record<string, string>;
}

export const FITBContentEdit: React.FC<FITBContentEditProps> = ({
    value: data,
    onChange,
    config,
    onConfigChange,
    disabled = false,
    editorOptions,
    configSlot,
    className,
    errors,
}) => {
    const { t } = useLocale({ config: config?.i18n });
    const [activeTab, setActiveTab] = React.useState<TabValue>('content');

    const safeData = normalizeFITBContentData(data ?? defaultFITBContentData);
    const exercises = safeData.exercises;

    const updateData = (updates: Partial<FITBContentData>) => {
        onChange(serializableFITBContentData(normalizeFITBContentData({ ...safeData, ...updates })));
    };

    const contentSectionCount = safeData.lessonContent?.sections?.length ?? 0;

    const [selectedId, setSelectedId] = React.useState<string | null>(null);
    const [selectedSectionId, setSelectedSectionId] = React.useState<string | null>(null);

    // EAA-2083: only the exercise selection follows `exercises`; the section selection is left
    // alone. Every sidebar-rows change (typing a section title included) rebuilds `exercises`
    // as a new array, so with no exercises yet this effect cleared the selected section on
    // the first keystroke and closed its panel. A section that is really removed is
    // deselected by CompositeEditSortableQuestionsLayout itself.
    React.useEffect(() => {
        if (exercises.length === 0) {
            setSelectedId(null);
            return;
        }
        setSelectedId((prev) => {
            if (prev && exercises.some((i) => i.id === prev)) {
                return prev;
            }
            return exercises[0].id;
        });
    }, [exercises]);

    const handleLessonChange = (lessonContent: PageElementData) => {
        updateData({ lessonContent });
    };

    const addExercise = () => {
        if (disabled) {
            return;
        }
        const next: FITBContentExercise = {
            id: generateId(),
            data: { ...defaultFITBElementData },
        };
        const nextEx = [...exercises, next];
        updateData({
            exercises: nextEx,
            questionsSidebarRows: persistQuestionsSidebarRows(
                reconcileQuestionsSidebarRows(safeData.questionsSidebarRows, map(nextEx, (e) => e.id))
            ),
        });
        setSelectedId(next.id);
        setSelectedSectionId(null);
        setActiveTab('questions');
    };

    const removeExercise = (itemId: string) => {
        if (disabled || exercises.length <= 1) {
            return;
        }
        const nextEx = exercises.filter((i) => i.id !== itemId);
        updateData({
            exercises: nextEx,
            questionsSidebarRows: persistQuestionsSidebarRows(
                reconcileQuestionsSidebarRows(safeData.questionsSidebarRows, map(nextEx, (e) => e.id))
            ),
        });
    };

    const updateExercise = (itemId: string, next: FITBContentExercise) => {
        if (disabled) {
            return;
        }
        updateData({
            exercises: map(exercises, (it) => (it.id === itemId ? next : it)),
        });
    };

    const handleQuestionsSidebarRowsChange = (rows: CompositeQuestionsSidebarRow[]) => {
        if (disabled) {
            return;
        }
        const nextEx = orderItemsByQuestionsSidebarRows(exercises, rows);
        updateData({
            exercises: nextEx,
            questionsSidebarRows: persistQuestionsSidebarRows(rows),
        });
    };

    const handleAddSidebarSection = () => {
        if (disabled) {
            return;
        }
        const sid = generateId();
        const base = resolvedQuestionsSidebarRows(
            safeData.questionsSidebarRows,
            map(exercises, (e) => e.id)
        );
        handleQuestionsSidebarRowsChange([
            {
                kind: 'section',
                section: { id: sid, title: '', description: createEmptyDocument() },
            },
            ...base,
        ]);
        setSelectedSectionId(sid);
        setSelectedId(null);
    };

    const sidebarRows = React.useMemo(
        () => resolvedQuestionsSidebarRows(safeData.questionsSidebarRows, map(exercises, (e) => e.id)),
        [exercises, safeData.questionsSidebarRows]
    );


    const fitbAddOptions = [
        { id: FITB_EXERCISE_TYPE_ID, label: t('addExerciseOption', 'Fill in the blanks') },
    ];

    const tabs: EditTabDescriptor[] = [
        { id: 'content', label: t('contentTab', 'Content'), icon: FileText, badge: contentSectionCount },
        {
            id: 'questions',
            label: t('questionsTab', 'Questions'),
            icon: TextCursorInput,
            badge: exercises.length,
        },
        ...(configSlot ? [{ id: 'config', label: t('configTab', 'Configuration'), icon: Settings }] : []),
    ];

    const headerMeta = (
        <div className="fitb-content-edit-header space-y-4">
            {editorOptions?.showTitle !== false && (
                <div className="space-y-2">
                    {/* EAA-2066: Title is required on save (useContentDetails) — mark it like
                        the other content types do. */}
                    <Label htmlFor="fitb-content-title">
                        {t('titleLabel', 'Title')} <span className="text-destructive">*</span>
                    </Label>
                    <Input
                        id="fitb-content-title"
                        value={safeData.title || ''}
                        onChange={(e) => updateData({ title: e.target.value })}
                        placeholder={t('titlePlaceholder', 'Enter title')}
                        disabled={disabled}
                        required
                        className="fitb-content-edit-title-input text-lg font-semibold"
                    />
                </div>
            )}
            {editorOptions?.showDescription !== false && (
                <div className="space-y-2">
                    <Label htmlFor="fitb-content-description">{t('descLabel', 'Instructions (Optional)')}</Label>
                    <Textarea
                        id="fitb-content-description"
                        value={safeData.description || ''}
                        onChange={(e) => updateData({ description: e.target.value })}
                        placeholder={t('descPlaceholder', 'Enter instructions...')}
                        disabled={disabled}
                        rows={3}
                        className="fitb-content-edit-description-input"
                    />
                </div>
            )}
        </div>
    );

    const hideHeader =
        editorOptions?.showTitle === false && editorOptions?.showDescription === false;

    return (
        <CompositeEditShell
            prefix="fitb-content-edit"
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={(id) => setActiveTab(id as TabValue)}
            configSlot={configSlot}
            className={cn('fitb-content-edit h-full min-h-0', className)}
            renderPanel={(tabId) => {
                if (tabId === 'content') {
                    if (hideHeader) {
                        return (
                            <div className="fitb-content-edit-content fitb-content-edit-lesson-panel flex-1 min-h-0 overflow-y-auto px-1">
                                <PageEdit
                                    value={safeData.lessonContent}
                                    onChange={handleLessonChange}
                                    disabled={disabled}
                                    hideSettings
                                    allowedBlockTypes={PAGE_BLOCK_TYPES}
                                    className="fitb-content-edit-lesson-page-inner"
                                />
                            </div>
                        );
                    }
                    return (
                        <CompositeEditContentTab
                            prefix="fitb-content-edit"
                            meta={headerMeta}
                            page={
                                <PageEdit
                                    value={safeData.lessonContent}
                                    onChange={handleLessonChange}
                                    disabled={disabled}
                                    hideSettings
                                    allowedBlockTypes={PAGE_BLOCK_TYPES}
                                    className="fitb-content-edit-lesson-page-inner"
                                />
                            }
                        />
                    );
                }
                if (tabId === 'questions') {
                    return (
                        <CompositeEditSortableQuestionsLayout
                            prefix="fitb-content-edit"
                            listTitle={t('questionsListTitle', 'Questions')}
                            emptyMessage={t('emptyQuestionsMsg', 'No questions yet. Add a fill-in-the-blanks exercise.')}
                            items={exercises}
                            selectedId={selectedId}
                            onSelectId={setSelectedId}
                            selectedSectionId={selectedSectionId}
                            onSelectSectionId={setSelectedSectionId}
                            disabled={disabled}
                            minItemsToKeep={1}
                            sidebarRows={sidebarRows}
                            onSidebarRowsChange={handleQuestionsSidebarRowsChange}
                            onAddSidebarSection={handleAddSidebarSection}
                            addSidebarSectionLabel={t('addSection', 'Add section')}
                            removeSidebarSectionLabel={t('removeSection', 'Remove section')}
                            sectionDetailTitle={t('sectionDetailTitle', 'Question section')}
                            onRemoveItem={removeExercise}
                            itemHasValidationError={(index) =>
                                hasErrorsForExerciseIndex(errors, index)
                            }
                            getItemLabel={(itemId, index) => {
                                const ex = exercises.find((e) => e.id === itemId);
                                if (!ex) return '';
                                return exercisePreview(ex, index, t);
                            }}
                            addButtonLabel={t('addExerciseLabel', 'Add exercise')}
                            addButtonOptions={fitbAddOptions}
                            addButtonClassName="fitb-content-edit-add-exercise"
                            onAddItem={() => addExercise()}
                            renderDetailHeader={({ itemId, index }) => {
                                const ex = exercises.find((e) => e.id === itemId);
                                return (
                                    <h3 className="text-lg font-semibold text-foreground">
                                        {ex
                                            ? exercisePreview(ex, index, t)
                                            : t('questionNumber', 'Question {{index}}').replace('{{index}}', String(index + 1))}
                                    </h3>
                                );
                            }}
                            renderItemDetail={({ itemId, index }) => {
                                const ex = exercises.find((e) => e.id === itemId);
                                if (!ex) {
                                    return null;
                                }
                                const elementErrors = pickErrorsUnderPrefix(
                                    errors,
                                    exerciseDataErrorsPrefix(index)
                                );
                                return (
                                    <FITBElementEdit
                                        value={ex.data}
                                        onChange={(d) =>
                                            updateExercise(ex.id, {
                                                ...ex,
                                                data: d,
                                            })
                                        }
                                        config={config}
                                        onConfigChange={onConfigChange}
                                        disabled={disabled}
                                        errors={elementErrors}
                                        className="fitb-content-edit-element-inner"
                                    />
                                );
                            }}
                            detailContent={null}
                            detailEmptyMessage={t('detailEmptyMessage', 'Select a question, a section, or add one to start.')}
                            richTextTranslate={t}
                        />
                    );
                }
                return null;
            }}
        />
    );
};

export default FITBContentEdit;
